import { type FC, useState } from 'react';
import { Formik, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  type UiHStackProps,
  UiStack
} from '@/lib/ui';
import BaseFormDrawer from '@/base/Form/Drawer';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseFormInputField from '@/base/Form/InputField';
import BaseFormTextareaField from '@/base/Form/TextareaField';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse } from '@/api/tenantClient';
import { type EventSetting, type EventSettingsUpdateRequest } from '@/api/registration';
import { registration } from '@/api';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import QueryContainer from '@/base/QueryContainer/QueryContainer';

export interface LabelsFormProps extends UiHStackProps {
  onClose: () => void
  onSaveSuccess: () => void
  isVisible: boolean
}

interface FormData {
  attendeeCategoryLabel?: string
  attendeeCategoryPageDescription?: string
  discountCodeLabel?: string
  idInformationLabel?: string
  idPhotocopyLabel?: string
}

const formSchema = Yup.object().shape({
  attendeeCategoryLabel: Yup.string()
    .nullable(),
  attendeeCategoryPageDescription: Yup.string()
    .max(500, 'Description can not have more than 500 charactors.')
    .nullable(),
  discountCodeLabel: Yup.string()
    .nullable(),
  idInformationLabel: Yup.string()
    .nullable(),
  idPhotocopyLabel: Yup.string()
    .nullable(),
});

const AttendeeCategoryPageForm: FC<LabelsFormProps> = ({
  onClose,
  isVisible
}) => {
  const { eventId } = useRegisterRoute();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const eventSettingQuery = useEventSettingsQuery(eventId);
  const { createTenantAdminApiRequest } = useTenantApi();
  const { mutateAsync: mutateEventSettings, isLoading } = useMutation<ApiResponse<EventSetting>, Error, EventSettingsUpdateRequest>({
    mutationFn: async (data: EventSettingsUpdateRequest) => {
      return await registration.updateEventSettings(createTenantAdminApiRequest)(data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [registration.eventSettingsQueryKey, { eventId }] });
      onClose();
    },
    onError: (error) => {
      setSaveErrors([error.message ?? 'Failed to save the attendee group labels.']);
    }
  });

  const submitForm = async (values: FormData) => {
    await mutateEventSettings({
      eventId,
      config: {
        pageMetadata: {
          ...eventSettingQuery.data!.pageMetadata,
          attendeeCategory: {
            title: values.attendeeCategoryLabel,
            description: values.attendeeCategoryPageDescription,
            discountCodeLabel: values.discountCodeLabel,
            idInformationLabel: values.idInformationLabel,
            idPhotocopyLabel: values.idPhotocopyLabel,
          }
        }
      }
    });
  };

  return (
    <QueryContainer query={eventSettingQuery} loadingComponent={<></>}>
      {(eventSetting) => {
        return (
          <Formik<FormData>
            initialValues={{
              attendeeCategoryLabel: eventSetting.pageMetadata?.attendeeCategory?.title,
              attendeeCategoryPageDescription: eventSetting.pageMetadata?.attendeeCategory?.description,
              idInformationLabel: eventSetting.pageMetadata?.attendeeCategory?.idInformationLabel,
              discountCodeLabel: eventSetting.pageMetadata?.attendeeCategory?.discountCodeLabel,
              idPhotocopyLabel: eventSetting.pageMetadata?.attendeeCategory?.idPhotocopyLabel
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={formSchema}
            onSubmit={async (
              values: FormData,
              { setSubmitting }: FormikHelpers<FormData>
            ) => {
              setSubmitting(true);
              await submitForm(values);
              setSubmitting(false);
            }}
          >
            <BaseFormDrawer
              isOpen={isVisible}
              onClose={onClose}
              title={'Attendee group page'}
              size={'xl'}
              isLoading={isLoading}
            >
              {saveErrors.length > 0 && (
                <UiStack spacing={4} flexGrow={1} py={4}>
                  {saveErrors.map((error, index) => {
                    return (
                      <BaseMessageBarError key={index}>
                        {error}
                      </BaseMessageBarError>
                    );
                  })}
                </UiStack>
              )}
              <BaseFormFieldGroup>
                <BaseFormInputField
                  name={'attendeeCategoryLabel'}
                  label={'Label for attendee group'}
                  isRequired={false}
                  helperText={'The label do you want to use instead of "Attendee group".'} />
                <BaseFormTextareaField
                  name={'attendeeCategoryPageDescription'}
                  label={'Description/instruction for the page'}
                  isRequired={false}
                  maxLength={500}
                  helperText={'The description or instruction you want to show on the top of the page.'} />
                <BaseFormInputField
                  name={'discountCodeLabel'}
                  label={'Label for discount code'}
                  isRequired={false}
                  helperText={'The label the attendee will see for the discount code field, by default this is "Discount code".'} />
                <BaseFormInputField
                  name={'idPhotocopyLabel'}
                  label={'Label for ID photocopy'}
                  isRequired={false}
                  helperText={"The label the attendee will see for the ID image field if you have set it to required for a given discount, for example: 'Photocopy of Student ID card'"} />
                <BaseFormInputField
                  name={'idInformationLabel'}
                  label={'Label for ID information'}
                  isRequired={false}
                  helperText={"The label the attendee will see for the ID text field if you have it to required for a given discount, for example: 'Association Membership number'."} />
              </BaseFormFieldGroup>
            </BaseFormDrawer>
          </Formik>
        );
      }}
    </QueryContainer>

  );
};

export default AttendeeCategoryPageForm;
